// Type contracts mirroring the FastAPI responses in `src/interior/server.py`.
// Keep these in sync with that file.

// `generated` / `published` are the manual-verification review statuses:
// a job finishes generation but is held for human review (`generated`),
// then the reviewer publishes the approved crops (`published`). They join
// the original live/terminal statuses because the History screen now
// lists review rows too.
export type JobStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'generated'
  | 'published';

/**
 * Per-FAL-endpoint usage row, identical wire shape to lightning's +
 * video's `FalEndpointUsage` (the unified tracker lives in
 * `src/shared/usage_tracker.py` and is re-exported by all three apps).
 */
export interface FalEndpointUsage {
  endpoint: string;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  /** Dollar amount FAL reports back in `usage.cost`. */
  direct_cost: number;
}

/**
 * Per-scene crop reference returned by `GET /api/interior/api/jobs/{id}/images`.
 * The interior pipeline produces one entry per scene name (e.g. `driver_seat`,
 * `radio`); the URL is either a presigned S3 link (storage_mode='s3'/'both')
 * or a path under `/api/local-files/...` (storage_mode='local').
 */
export type SceneUrlMap = Record<string, string>;

/**
 * Shape returned by `GET /api/interior/api/jobs/{id}/images`. Always
 * contains both crop variants; one or both can be empty when the
 * pipeline emitted no enhanced output (e.g. ODD failure with
 * `skip_outputs_on_odd_failure=true`).
 */
export interface JobImages {
  originalCrops: SceneUrlMap;
  enhancedCrops: SceneUrlMap;
  /**
   * Full-panorama original + enhanced equirectangular images, surfaced
   * above the crops grid in a synchronized 360 viewer. Each variant is
   * absent when the pipeline didn't produce it (full-image enhancement
   * disabled, or the enhancement call failed).
   */
  fullImage?: { original?: string; enhanced?: string } | null;
  oddPassed: boolean | null;
  errorScenes: Record<string, string> | null;
}

/**
 * Vehicle metadata captured during the apollo cropper run. Best-effort
 * (fields are `null` whenever the uvcamp inspection payload omits them).
 *
 * 1:1 mirror of the Python `VehicleMetaData` Pydantic model in
 * `src/interior/data_types.py`, which itself mirrors the dataclass in
 * `src/shared/uvcamp/inspection.py`. Field names stay snake_case
 * because they're nested inside the PascalCase `VehicleInfo` key of
 * `InteriorScanResult`.
 */
export interface VehicleMetaData {
  make: string | null;
  model: string | null;
  year: string | null;
  color: string | null;
  vehicle_type: string | null;
  vin: string | null;
  fuel_type: string | null;
}

/**
 * Canonical job-result payload returned on `Job.result` for completed
 * scans. Mirrors the Python `InteriorScanResult` Pydantic model
 * (`src/interior/data_types.py`), which is the alias-tolerant schema
 * stored in `interior_jobs.result` (JSONB). All keys are PascalCase to
 * match lightning's `LightningServiceResponse` wire convention; the
 * backend re-emits the canonical shape via `Job.to_dict()` so pre-rename
 * rows (`results_path`, `errorScenes`, `vehicle_info`) are normalised
 * before they reach the wire.
 */
export interface InteriorScanResult {
  /**
   * ISO-8601 UTC timestamp UVCamp posted on the request, indicating
   * when the rig captured the panorama. `null` when the caller didn't
   * supply it.
   */
  AcquisitionStartedAtUTC: string | null;
  /**
   * Duplicate of the callback envelope's `organization_id`, surfaced
   * in PascalCase for lightning parity.
   */
  OrgId: string | null;
  /**
   * Duplicate of the callback envelope's `site_id`, surfaced in
   * PascalCase for lightning parity.
   */
  SiteId: string | null;
  ScanId: string | null;
  /** S3 bucket name the run wrote to. `null` in local storage mode. */
  S3Bucket: string | null;
  /**
   * Actual home region of `S3Bucket`, resolved from the bucket itself
   * (`head_bucket`'s `x-amz-bucket-region`) and falling back to
   * `AWS_DEFAULT_REGION`. Lets consumers locate the bucket without an
   * extra `GetBucketLocation` round trip.
   */
  S3Region: string | null;
  /** Local absolute path or `s3://bucket/key/prefix` URI for the run. */
  ResultsPath: string | null;
  /**
   * Per-scene URL map produced by `InteriorStorageBackend.build_file_mapping`.
   * Shape: `{originalCrops: {scene: {path}}, enhancedCrops: {scene: {path}}}`.
   * The `/jobs/{id}/images` endpoint reshapes this into presigned URLs.
   */
  RenderedFilePath: Record<string, unknown> | null;
  /** False when the ODD check tripped (e.g. windshield wrapped across the seam). */
  OddPassed: boolean | null;
  /**
   * Managed background composited behind the interior, echoed back for
   * traceability. The interior-only `'none'` sentinel means no
   * replacement was applied (the original image background was kept);
   * `null` means the request predates the managed-background feature.
   */
  BackgroundName?: string | null;
  /**
   * Per-scene map `scene → S3 path/key (or local path)` of the JSON file
   * holding the exact FAL request used for that scene's FIRST
   * enhancement attempt (replayable via `fal_client.submit`). `null` for
   * runs predating this feature or that produced no enhanced scenes.
   */
  GenerationPayloads?: Record<string, string> | null;
  /** Map of scene name → reason for scenes the pipeline skipped. */
  ErrorScenes: Record<string, string> | null;
  VehicleInfo: VehicleMetaData | null;
}

/**
 * One managed interior background, returned by
 * `GET /api/interior/api/settings/backgrounds`. Interior-scoped mirror
 * of lightning's `PhotoboothImage` (no `prompt_metadata`).
 *
 * The synthetic `'none'` entry (empty `filename`) is the interior-only
 * "No background" choice: selecting it sets `replace_background: false`
 * so the original image background is preserved.
 */
export interface InteriorBackground {
  name: string;
  is_default: boolean;
  filename: string;
}

/**
 * Per-request generation settings for the interior pipeline. Mirrors the
 * backend `InteriorScanSettings`. Sent on `/scans` and `/api/batches`.
 */
export interface InteriorScanSettings {
  clean_vehicle: boolean;
  /** Master switch for managed-background replacement. */
  replace_background: boolean;
  /** Per-request override for full 360 panorama enhancement. */
  enhance_dynamic_360: boolean;
  /** Per-request override for per-scene crop enhancement. */
  enhance_crops: boolean;
  /** When true, hold the generation for manual review. */
  manual_review: boolean;
}

/** Reserved background name for the "No background" option. */
export const NONE_BACKGROUND = 'none';

/**
 * One row in the interior jobs table — the JSON shape produced by
 * `interior.jobs.Job.to_dict()`. Mirrors lightning's + video's job
 * envelopes so the stats/history components can be reused.
 *
 * The optional ``elapsed_seconds`` is populated only by
 * ``GET /api/jobs/live`` (computed server-side from ``started_at``)
 * and stays ``null``/``undefined`` for history rows so the live-scans
 * timer cell has a definitive starting value on every poll.
 */
export interface Job {
  job_id: string;
  scan_id: string;
  status: JobStatus;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  /**
   * Set only on manual-verification rows once the reviewer publishes the
   * approved crops (status `published`). `null` for every other row,
   * including `generated` rows still awaiting review.
   */
  published_at?: string | null;
  duration_seconds: number | null;
  error: string | null;
  organization_id: string | null;
  site_id: string | null;
  uvcamp_link: string | null;
  source: string | null;
  service_version: string | null;
  /** See {@link InteriorScanResult}. `null` for pending / failed rows. */
  result: InteriorScanResult | null;
  /** See {@link FalEndpointUsage}. `null` for jobs that finished before billing instrumentation existed. */
  fal_usage: Record<string, FalEndpointUsage> | null;
  batch_id: string | null;
  /** Set on live rows only — see {@link Job} docs. */
  elapsed_seconds?: number | null;
}

/** Shape returned by the interior service's `/health` endpoint. */
export interface InteriorHealth {
  status: string;
  version: string;
  git_commit?: string | null;
  git_branch?: string | null;
  output_dir: string;
  counts: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  };
}

export interface CreateScanResponse {
  job_id: string;
  scan_id: string;
  status: JobStatus;
}

/**
 * Summary row returned by `/api/interior/api/batches` (active batches).
 *
 * Shape matches lightning's ``BatchRun`` so the `BatchRunsTable`
 * component can be cloned verbatim. ``completion_percentage`` is
 * ``(completed + failed) / total_scans * 100``; ``success_percentage``
 * is ``completed / total_scans * 100``. Both are computed server-side
 * in :func:`interior.jobs.JobStore.batch_summary`.
 */
export interface BatchSummary {
  batch_id: string;
  filename: string;
  total_scans: number;
  created_at: string;
  completed_at: string | null;
  pending: number;
  in_progress: number;
  completed: number;
  failed: number;
  completion_percentage: number;
  success_percentage: number;
}

/**
 * One row in the paginated batch-history table, returned by
 * `GET /api/interior/api/batches/history`. Shape mirrors lightning's
 * `BatchHistoryItem` exactly so the same React table component works.
 */
export interface BatchHistoryItem {
  batch_id: string;
  filename: string;
  total_scans: number;
  completed: number;
  failed: number;
  success_percentage: number;
  duration_seconds: number | null;
  created_at: string | null;
  completed_at: string | null;
  /** Sum of every job's FAL `direct_cost`. */
  total_cost: number | null;
}

export interface BatchHistoryResponse {
  batches: BatchHistoryItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/**
 * Paginated history envelope returned by
 * `GET /api/interior/api/jobs/history`. Mirrors lightning's
 * `HistoryResponse` so we can reuse pagination + filtering UI verbatim.
 */
export interface HistoryResponse {
  jobs: Job[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/** Inclusive date filter shape shared with the date-range picker. */
export interface DateRange {
  from: Date | null;
  to: Date | null;
}

export interface BatchCreatedResponse {
  batch_id: string;
  filename: string;
  total_scans: number;
  message: string;
}

/** Shape returned by `GET /api/interior/api/settings/config-yaml`. */
export interface InteriorConfigYaml {
  path: string;
  yaml: string;
  parsed: Record<string, unknown>;
}

/**
 * Deployment-time delivery mode mirrored from the Python
 * `GenerationCallbackMode` enum (`src/interior/pipeline_config.py`).
 *
 * - `automatic`: finished generations are delivered immediately; the
 *   Manual Review tab is irrelevant and gets disabled in the UI.
 * - `manual_verification`: generations are held for human review, so the
 *   Manual Review tab is enabled.
 */
export type GenerationCallbackMode = 'automatic' | 'manual_verification';

/** Shape returned by `GET /api/interior/api/interior-config`. */
export interface InteriorConfig {
  generation_callback_mode: GenerationCallbackMode;
}

// ----- Manual verification ------------------------------------------------

/**
 * One row in the Manual Verification review-queue table, returned inside
 * the `jobs` array of `GET /api/interior/api/manual-verification`.
 *
 * The wire shape is a normal interior {@link Job} dict scoped to the two
 * review statuses (`generated` / `published`), so we reuse `Job`
 * verbatim (it already carries `scan_id`, `uvcamp_link`, `source`,
 * `organization_id`, `status`, `completed_at`, `published_at`, `result`
 * and `job_id`). The alias exists for call-site readability.
 */
export type ManualVerificationRow = Job;

/** Per-scene review status toggled by the reviewer's Approve action. */
export type ReviewSceneStatus = 'in_progress' | 'approved';

/**
 * One crop scene in the per-scan review editor. Mirrors the scene object
 * returned by `GET /api/interior/api/jobs/{id}/review` and echoed back by
 * every per-scene action endpoint (approve / regenerate / prompt / undo /
 * redo), which each return the SINGLE updated scene with this exact shape.
 */
export interface ReviewScene {
  scene: string;
  /**
   * Current-version crop URL (presigned S3 link or `/api/local-files`
   * path). The review page cache-busts this with a `?t=<ts>` suffix after
   * an action so the browser refetches the freshly written bytes.
   */
  url: string;
  /**
   * Raw (un-enhanced) apollo crop URL — `result.RenderedFilePath`'s
   * `originalCrops[scene]`, resolved with the same helper as `url`. It
   * never changes across versions and powers the fullscreen "Raw crop"
   * panel shown beside the "Current" image. `null` when the scene has no
   * original crop on record.
   */
  rawUrl: string | null;
  status: ReviewSceneStatus;
  /** Pointer can move back a version (a prior regenerate/prompt exists). */
  canUndo: boolean;
  /** Pointer can move forward a version (a truncated-future version exists). */
  canRedo: boolean;
  /**
   * Whether `result.GenerationPayloads[scene]` exists so Regenerate can
   * replay it. Derived/full-panorama scenes have no payload -> Regenerate
   * is disabled for them.
   */
  hasPayload: boolean;
}

/**
 * Full review payload for one scan, returned by
 * `GET /api/interior/api/jobs/{id}/review`. The `scenes` are crops only
 * (no panorama). `status` is the job-level review status.
 */
export interface ScanReview {
  job_id: string;
  scan_id: string;
  organization_id: string | null;
  uvcamp_link: string | null;
  status: 'generated' | 'published';
  scenes: ReviewScene[];
}

/** Shape returned by `POST /api/interior/api/jobs/{id}/publish`. */
export interface PublishResponse {
  published: number;
}
